import AppPagination from "@/component/common/AppPagination.tsx";
import ManageContentLayout from "@/component/common/layout/ManageContentLayout.tsx";
import {
    Button,
    DatePicker,
    Descriptions,
    Flex,
    Form,
    Modal,
    Select,
    Space,
    Table,
    TablePaginationConfig,
    TableProps,
    Tag,
    Tooltip,
} from "antd";
import { TFunction } from "i18next";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import ConfirmButton from "@/component/common/button/ConfirmButton";
import ContentParagraph from "@/component/ContentParagraph.tsx";
import { StringHelper } from "@/constant/helper.ts";
import { ConversionRecordService } from "@/services/conversion-record/index.ts";
import { toast } from "@/utils/toast.tsx";
import { InfoCircleOutlined } from "@ant-design/icons";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
import dayjs from "dayjs";

/**
 * 下载转换文件
 * @param type 下载文件类型 1-转换前文件 2-转换后文件 3-转换失败文件
 * @param conversionLogId 代码转换日志记录ID
 * @param filename 文件名称（含后缀）
 */
async function onDownload(
  type: number,
  conversionLogId: string,
  filename: string,
) {
  try {
    // 请求API获取文件流
    const blob = await ConversionRecordService.download({
      conversionLogId,
      type,
    });

    // 创建下载链接
    const link = document.createElement("a");
    link.download = filename;
    link.style.display = "none";
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    link.href = URL.createObjectURL(blob);
    document.body.appendChild(link);
    link.click();

    // 清理资源
    setTimeout(() => {
      URL.revokeObjectURL(link.href);
      document.body.removeChild(link);
    }, 100);
  } catch (error) {
    console.error("下载文件失败:", error);
    throw error;
  }
}

const generateHeaderTable = (
  t: TFunction,
  research: any,
): TableProps<Record<string, any>>["columns"] => {
  return [
    {
      title: t("field.no"),
      dataIndex: "no",
      key: "no",
      align: "center",
      render: (_, _record, index) => <span>{index + 1}</span>,
    },
    {
      title: t("field.conversion_type"),
      dataIndex: "conversionType",
      key: "conversionType",
      // render: (_, { conversionType }) =>
      //   ({
      //     1: "Cobol -> Java",
      //     2: "Delphi -> C#",
      //     3: "Asp -> Jsp + Java",
      //     4: "Aspx -> Jsp + Java",
      //     5: "Jsp -> Thymeleaf",
      //     6: "VB.NET -> Java",
      //   }[conversionType as never] || "-"),
    },
    {
      title: t("field.request_ip"),
      dataIndex: "requestIp",
      key: "requestIp",
    },
    {
      title: t("field.request_time"),
      dataIndex: "requestTime",
      key: "requestTime",
    },
    {
      title: t("field.conversion_status"),
      key: "conversionStatus",
      dataIndex: "conversionStatus",
      align: "center",
      render: (_, { conversionStatus, conversionStatusText, syncMsg }) => (
        <Tag color={{ 0: "error", 1: "success" }[conversionStatus as never]}>
          {conversionStatusText}
          {conversionStatus != 1 && syncMsg ? (
            <Tooltip title={syncMsg}>
              <InfoCircleOutlined className="cursor-pointer ml-[3px]" />
            </Tooltip>
          ) : null}
        </Tag>
      ),
    },
    {
      title: t("field.file_url_before"),
      dataIndex: "fileUrlBefore",
      key: "fileUrlBefore",
      render: (_, { conversionLogId, fileUrlBefore }) => (
        <Button
          type="link"
          onClick={() => onDownload(1, conversionLogId, fileUrlBefore)}
        >
          {t("field.download")}
        </Button>
      ),
    },
    {
      title: t("field.file_url_after"),
      dataIndex: "fileUrlAfter",
      key: "fileUrlAfter",
      render: (_, { conversionLogId, fileUrlAfter }) =>
        fileUrlAfter ? (
          <Button
            type="link"
            onClick={() => onDownload(2, conversionLogId, fileUrlAfter)}
          >
            {t("field.download")}
          </Button>
        ) : (
          "-"
        ),
    },
    {
      title: t("field.file_url_failed"),
      dataIndex: "fileUrlFailed",
      key: "fileUrlFailed",
      render: (_, { conversionLogId, fileUrlFailed }) =>
        fileUrlFailed ? (
          <Button
            type="link"
            onClick={() => onDownload(3, conversionLogId, fileUrlFailed)}
          >
            {t("field.download")}
          </Button>
        ) : (
          "-"
        ),
    },
    {
      title: t("field.conversion_time"),
      dataIndex: "conversionTime",
      key: "conversionTime",
      render: (_, { conversionTime }) => (
        <div className="text-center">{conversionTime || 0}ms</div>
      ),
    },
    {
      title: t("field.action"),
      key: "action",
      align: "center",
      hidden: true,
      render: (_, record) => (
        <Space size="middle">
          {record ? null : <DetailButton record={record} />}
          <DeleteButton record={record} research={research} />
        </Space>
      ),
    },
  ];
};

const DetailButton = ({ record }: { record: ContactDetail }) => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleCancel = () => {
    setIsOpen(false);
  };

  return (
    <>
      <Button
        color={"primary"}
        variant={"filled"}
        onClick={() => setIsOpen(true)}
      >
        {t("button.detail")}
      </Button>
      <Modal
        title={<h2>{t("title.customerDetail")}</h2>}
        width={999}
        open={isOpen}
        onCancel={handleCancel}
        footer={
          <Button type={"primary"} onClick={handleCancel} size={"large"}>
            {t("button.backList")}
          </Button>
        }
        style={{ top: 40 }}
        styles={{ body: { maxHeight: 685, overflow: "auto" } }}
      >
        <Descriptions
          labelStyle={{
            fontSize: 16,
            color: "#172635",
            fontWeight: 500,
            width: 200,
          }}
          contentStyle={{
            color: "#556476",
            fontSize: 16,
          }}
          colon={false}
          column={1}
          items={[
            {
              key: "firstName",
              label: t("field.firstName"),
              children: record.firstName,
            },
            {
              key: "lastName",
              label: t("field.lastName"),
              children: record.lastName,
            },
            {
              key: "companyName",
              label: t("field.companyName"),
              children: record.companyName,
            },
            {
              key: "departmentName",
              label: t("field.departmentName"),
              children: record.departmentName,
            },
            {
              key: "email",
              label: t("field.email"),
              children: record.email,
            },
            {
              key: "mobile",
              label: t("field.mobile"),
              children: record.mobile,
            },
            {
              key: "message",
              label: t("field.inquiryDetail"),
              children: (
                <div className={"w-full max-h-[409.17px] overflow-x-auto"}>
                  <ContentParagraph content={record.message || ""} />
                </div>
              ),
            },
          ]}
        />
      </Modal>
    </>
  );
};

const DeleteButton = ({
  record,
  research,
}: {
  record: Record<string, any>;
  research: () => void;
}) => {
  const { t } = useTranslation();

  const handleDeleteUser = async () => {
    if (record?.conversionLogId) {
      const res: SuccessResponse | null | undefined =
        await ConversionRecordService.delete(record?.conversionLogId);
      if (res) {
        research();
        toast.success(res?.message || "Success");
      }
    }
  };

  return (
    <ConfirmButton
      buttonText={t("button.delete")}
      buttonProps={{
        color: "danger",
        variant: "filled",
      }}
      confirmMessage={t("tip.sync_remove")}
      handleOk={() => handleDeleteUser()}
    />
  );
};

const View = () => {
  const { t } = useTranslation();

  const isFirstRun = useRef<boolean>(true);
  const [dataTable, setDataTable] = useState<ContactDetail[]>([]);
  const [totalRecord, setTotalRecord] = useState<number>(0);
  const [searchRequest, setSearchRequest] = useState<SearchContactRequest>({
    size: 10,
    page: 0,
    sortBy: ["created_date"],
    sortDesc: [true],
  });
  const [searchForm] = Form.useForm();

  if (!searchForm.getFieldValue("conversionDate")) {
    searchForm.setFieldValue("conversionDate", [dayjs(), dayjs()]);
  }

  const handleSearch = async () => {
    const value = Object.assign({}, searchForm.getFieldsValue(), searchRequest);
    if (Array.isArray(value.conversionDate) && value.conversionDate.length) {
      const dateArr = value.conversionDate.map((e: any) =>
        e.format("YYYY-MM-DD"),
      );
      value.startRequestTime = dateArr[0];
      value.endRequestTime = dateArr[1];
    }
    const res: SearchCustomerResponse | null | undefined =
      await ConversionRecordService.search(value);
    if (!res?.items || res?.items?.length === 0) {
      setTotalRecord(0);
      setDataTable([]);
      if (searchRequest.page > 0) {
        onChangePageNum(1);
        return;
      }
      if (!isFirstRun.current) {
        toast.warning(t("message.noResultsFound"));
      }
      return;
    }
    setTotalRecord(res?.total || 0);
    setDataTable(res?.items);
  };

  const columns: TableProps<ContactDetail>["columns"] = generateHeaderTable(
    t,
    handleSearch,
  );

  const onChangePageNum = (page: number) => {
    setSearchRequest((prevState) => ({
      ...prevState,
      page: page - 1,
    }));
  };

  const onChangePageSize = (pageSize: number) => {
    setSearchRequest((prevState) => ({
      ...prevState,
      page: 0,
      size: pageSize,
    }));
  };

  const onChangeTable = (
    _pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ContactDetail> | SorterResult<ContactDetail>[],
  ) => {
    const sortValue = Array.isArray(sorter) ? sorter[0] : sorter;
    if (!sortValue.column) {
      setSearchRequest((prevState) => ({
        ...prevState,
        page: 0,
        sortBy: ["created_date"],
        sortDesc: [true],
      }));
    } else {
      setSearchRequest((prevState) => ({
        ...prevState,
        page: 0,
        sortDesc: [sortValue.order === "descend"],
        sortBy: [StringHelper.toSnakeCase(String(sortValue.columnKey))],
      }));
    }
  };

  useEffect(() => {
    handleSearch().finally(() => {
      if (isFirstRun.current) {
        isFirstRun.current = false;
      }
    });
  }, [searchRequest]);

  return (
    <>
      <ManageContentLayout>
        <div
          className={"p-5 rounded-lg border bg-white"}
          style={{ minHeight: "753px" }}
        >
          {/* Search block */}
          <Form
            form={searchForm}
            name="search-form"
            colon={false}
            onFinish={() => onChangePageNum(1)}
            size={"large"}
          >
            <Flex align="center">
              {/* <Search
              style={{ width: 555 }}
              placeholder={t("placeholder.search")}
              onSearch={onChangeKeySearch}
              enterButton
              size={"large"}
            /> */}
              <Form.Item
                name="conversionStatus"
                label={t("field.conversion_status")}
                className={"m-1"}
              >
                <Select
                  placeholder={t("placeholder.conversionStatus")}
                  options={[
                    { label: t("field.ok"), value: 1 },
                    { label: t("field.fail"), value: 0 },
                  ]}
                  allowClear
                  className="!w-[220px]"
                />
              </Form.Item>
              <Form.Item
                name="conversionDate"
                label={t("field.conversion_date")}
                className={"m-1"}
              >
                <DatePicker.RangePicker
                  className={"w-[255px]"}
                  allowClear={false}
                />
              </Form.Item>
              <Form.Item className={"text-end m-1"}>
                <Button type="primary" htmlType="submit" className="m-1">
                  {t("button.search")}
                </Button>
              </Form.Item>
            </Flex>
          </Form>
          {/* Table function block */}
          <div className={"mt-5"}>
            <Table
              rowKey={"conversionLogId"}
              columns={columns}
              dataSource={dataTable}
              pagination={false}
              onChange={onChangeTable}
            />
            <AppPagination
              className={"mt-3"}
              pageSize={searchRequest.size}
              onChangePageSize={onChangePageSize}
              onChangePage={onChangePageNum}
              currentPage={searchRequest.page}
              total={totalRecord}
            />
          </div>
        </div>
      </ManageContentLayout>
    </>
  );
};

export default View;
